import {
  MAX_PARTICLE_COUNT,
  getParticleBudget,
  type DeviceClass,
} from '../engine/particles/particleBudget.js'
import type { QualityTier, RenderBackendKind } from './RenderBackend.js'

export type { QualityTier } from './RenderBackend.js'

export interface MutableQualityDecision {
  tier: QualityTier
  effectiveDpr: number
  particleBudget: number
}

export interface AdaptiveQualityPolicy {
  read(requestedDpr: number, out: MutableQualityDecision): void
  sample(frameIntervalMs: number, workDurationMs: number): boolean
  reconfigure(backend: RenderBackendKind, deviceClass: DeviceClass): boolean
  resetTiming(): void
}

const TIERS = ['high', 'balanced', 'low', 'minimum'] as const satisfies readonly QualityTier[]
const DPR_MULTIPLIER: Record<QualityTier, number> = {
  high: 1,
  balanced: 0.875,
  low: 0.75,
  minimum: 0.5,
}
const PARTICLE_MULTIPLIER: Record<QualityTier, number> = {
  high: 1,
  balanced: 0.75,
  low: 0.5,
  minimum: 0.25,
}
const DPR_CAP: Record<DeviceClass, number> = {
  desktop: 2,
  mobile: 1.5,
  constrained: 1,
}

const EMA_ALPHA = 0.1
const DEGRADE_THRESHOLD_MS = 20
const IMPROVE_THRESHOLD_MS = 17
const DEGRADE_STREAK = 12
const IMPROVE_STREAK = 240
const TIER_CHANGE_COOLDOWN = 120
const MAX_ELIGIBLE_INTERVAL_MS = 100

function assertBackend(value: RenderBackendKind): void {
  if (value !== 'webgl2' && value !== 'canvas2d') {
    throw new RangeError(`Unsupported render backend: ${String(value)}`)
  }
}

function assertDeviceClass(value: DeviceClass): void {
  if (value !== 'desktop' && value !== 'mobile' && value !== 'constrained') {
    throw new RangeError(`Unsupported device class: ${String(value)}`)
  }
}

class AdaptiveQualityPolicyImplementation implements AdaptiveQualityPolicy {
  private tierIndex = 0
  private ema: number | null = null
  private slowStreak = 0
  private fastStreak = 0
  private cooldown = 0
  private backend: RenderBackendKind
  private deviceClass: DeviceClass
  private baseBudget: number

  constructor(backend: RenderBackendKind, deviceClass: DeviceClass) {
    assertBackend(backend)
    assertDeviceClass(deviceClass)
    this.backend = backend
    this.deviceClass = deviceClass
    this.baseBudget = getParticleBudget(backend, deviceClass)
  }

  read(requestedDpr: number, out: MutableQualityDecision): void {
    if (!Number.isFinite(requestedDpr) || requestedDpr <= 0) {
      throw new RangeError('Requested DPR must be finite and positive')
    }
    const tier = TIERS[this.tierIndex]
    if (tier === undefined) throw new RangeError('Adaptive quality tier is invalid')
    const clampedDpr = Math.min(Math.max(requestedDpr, 1), 4)
    const scaledDpr = Math.min(clampedDpr, DPR_CAP[this.deviceClass]) * DPR_MULTIPLIER[tier]
    const effectiveDpr = Math.max(1, Math.floor(scaledDpr * 4) / 4)
    const particleBudget = Math.min(
      MAX_PARTICLE_COUNT,
      Math.max(1, Math.floor(this.baseBudget * PARTICLE_MULTIPLIER[tier])),
    )
    out.tier = tier
    out.effectiveDpr = effectiveDpr
    out.particleBudget = particleBudget
  }

  sample(frameIntervalMs: number, workDurationMs: number): boolean {
    if (
      !Number.isFinite(frameIntervalMs)
      || !Number.isFinite(workDurationMs)
      || frameIntervalMs < 0
      || workDurationMs < 0
      || frameIntervalMs > MAX_ELIGIBLE_INTERVAL_MS
      || workDurationMs > MAX_ELIGIBLE_INTERVAL_MS
    ) return false

    const pressure = Math.max(frameIntervalMs, workDurationMs)
    this.ema = this.ema === null
      ? pressure
      : this.ema + EMA_ALPHA * (pressure - this.ema)

    if (this.cooldown > 0) {
      this.cooldown -= 1
      this.slowStreak = 0
      this.fastStreak = 0
      return false
    }

    if (this.ema >= DEGRADE_THRESHOLD_MS) {
      this.slowStreak += 1
      this.fastStreak = 0
      if (this.slowStreak >= DEGRADE_STREAK && this.tierIndex < TIERS.length - 1) {
        this.changeTier(this.tierIndex + 1)
        return true
      }
      return false
    }
    if (this.ema <= IMPROVE_THRESHOLD_MS) {
      this.fastStreak += 1
      this.slowStreak = 0
      if (this.fastStreak >= IMPROVE_STREAK && this.tierIndex > 0) {
        this.changeTier(this.tierIndex - 1)
        return true
      }
      return false
    }
    this.slowStreak = 0
    this.fastStreak = 0
    return false
  }

  reconfigure(backend: RenderBackendKind, deviceClass: DeviceClass): boolean {
    assertBackend(backend)
    assertDeviceClass(deviceClass)
    if (backend === this.backend && deviceClass === this.deviceClass) return false
    this.backend = backend
    this.deviceClass = deviceClass
    this.baseBudget = getParticleBudget(backend, deviceClass)
    this.resetTiming()
    return true
  }

  resetTiming(): void {
    this.ema = null
    this.slowStreak = 0
    this.fastStreak = 0
    this.cooldown = 0
  }

  private changeTier(nextTier: number): void {
    this.tierIndex = nextTier
    this.ema = null
    this.slowStreak = 0
    this.fastStreak = 0
    this.cooldown = TIER_CHANGE_COOLDOWN
  }
}

export function createAdaptiveQualityPolicy(
  backend: RenderBackendKind,
  deviceClass: DeviceClass,
): AdaptiveQualityPolicy {
  return new AdaptiveQualityPolicyImplementation(backend, deviceClass)
}
